import { useNavigate } from 'react-router-dom';
import { ArrowDown, ArrowUp, ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { expiryStatus } from '@/lib/license';
import { relativeTime } from '@/lib/format';
import { cn } from '@/lib/cn';
import type { Device } from '@/lib/types';

export type SortKey = 'name' | 'status' | 'license' | 'last_seen';
export interface SortState { key: SortKey; dir: 'asc' | 'desc' }

interface Props {
  devices: Device[];
  sort: SortState;
  onSortChange: (s: SortState) => void;
}

const columns: Array<{ key: SortKey; label: string; className?: string }> = [
  { key: 'name',      label: 'Device' },
  { key: 'status',    label: 'Status',    className: 'w-28' },
  { key: 'license',   label: 'License',   className: 'hidden md:table-cell w-52' },
  { key: 'last_seen', label: 'Last seen', className: 'hidden sm:table-cell w-32' },
];

// Dense one-line-per-device view for accounts that manage many controllers.
// Rows navigate like the cards do; the client id sits under the name so the
// table stays narrow enough for tablets.
export function DeviceTable({ devices, sort, onSortChange }: Props) {
  const navigate = useNavigate();

  const toggle = (key: SortKey) =>
    onSortChange(sort.key === key ? { key, dir: sort.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });

  return (
    <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
          <tr>
            {columns.map((c) => (
              <th key={c.key} className={cn('px-4 py-2.5 text-left font-semibold', c.className)}>
                <button onClick={() => toggle(c.key)}
                  className={cn('inline-flex items-center gap-1 hover:text-ink uppercase', sort.key === c.key && 'text-ink')}>
                  {c.label}
                  {sort.key === c.key && (sort.dir === 'asc'
                    ? <ArrowUp className="w-3 h-3" />
                    : <ArrowDown className="w-3 h-3" />)}
                </button>
              </th>
            ))}
            <th className="w-8" />
          </tr>
        </thead>
        <tbody>
          {devices.map((d) => {
            const online = !!d.online;
            const lic = expiryStatus(d.expires_at);
            const tone = lic.kind === 'expired' ? 'bad' : lic.kind === 'warn' ? 'warn' : lic.kind === 'active' ? 'ok' : 'neutral';
            const href = `/devices/${encodeURIComponent(d.client_id)}`;
            return (
              <tr key={d.client_id}
                onClick={() => navigate(href)}
                className="group border-b border-slate-100 last:border-0 hover:bg-slate-50 cursor-pointer">
                <td className="px-4 py-2.5 min-w-0">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className={cn('shrink-0 w-2 h-2 rounded-full', online ? 'bg-emerald-500' : 'bg-slate-300')} />
                    <div className="min-w-0">
                      {/* Real link so middle-click / open-in-new-tab work. */}
                      <a href={href} onClick={(e) => { e.preventDefault(); navigate(href); }}
                        className="block font-semibold text-ink truncate max-w-[16rem] sm:max-w-md">
                        {d.client_name}
                      </a>
                      <div className="text-[11px] text-slate-500 font-mono truncate">{d.client_id}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-2.5">
                  <Badge tone={online ? 'ok' : 'neutral'} dot>{online ? 'Online' : 'Offline'}</Badge>
                </td>
                <td className="px-4 py-2.5 hidden md:table-cell">
                  {lic.kind === 'none'
                    ? <span className="text-xs text-slate-400">—</span>
                    : <Badge tone={tone} className="whitespace-nowrap">{lic.text}</Badge>}
                </td>
                <td className="px-4 py-2.5 hidden sm:table-cell text-xs text-slate-500 tabular-nums whitespace-nowrap">
                  {relativeTime(d.last_seen)}
                </td>
                <td className="pr-3 text-right">
                  <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 inline" />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
